<template>
  <SrpModal
    :isVisible="props.isVisible"
    @update:isVisible="value => emit('update:isVisible', value)"
    size="large"
    className="first-posts-modal"
    :maxHeight="contentMaxHeight"
    :isClosable="false"
    :isCloseButtonVisible="!props.isSubmitting"
  >
    <template #header>
      <div class="first-posts-modal__header">
        <h2 class="global-h2 first-posts-modal__title">Let’s create your first posts</h2>
        <p class="global-p first-posts-modal__subtitle">Select places or events, add a little direction, or let us choose.</p>
      </div>
    </template>

    <template #content>
      <label class="first-posts-modal__note">
        <span class="first-posts-modal__note-label">Any direction for your posts? <span class="first-posts-modal__optional">(optional)</span></span>
        <!-- No tone example: the note steers which topics and photos get picked, not how captions are worded. -->
        <textarea
          v-model="note"
          class="global-textarea first-posts-modal__note-input"
          rows="3"
          :maxlength="FIRST_POSTS_NOTE_MAX_LENGTH"
          placeholder="e.g. Lead with our fall events and local food spots."
          :disabled="props.isSubmitting"
        ></textarea>
      </label>

      <section class="first-posts-modal__focus" aria-labelledby="first-posts-focus-title">
        <div class="first-posts-modal__focus-heading">
          <h3 id="first-posts-focus-title" class="first-posts-modal__focus-title">
            Or places and events to focus on?
            <!-- <span class="first-posts-modal__optional">(optional)</span> -->
          </h3>
          <div v-if="options.length" class="first-posts-modal__search">
            <button type="button" class="first-posts-modal__search-toggle" aria-label="Search places and events" :aria-expanded="isSearchExpanded" aria-controls="first-posts-search" @click="openSearch">
              <IconEmbedded name="magnifier_2-5" :size="16" color="#667085" />
            </button>
            <div class="first-posts-modal__search-input-wrap" :class="{ 'first-posts-modal__search-input-wrap--open': isSearchExpanded }">
              <input
                id="first-posts-search"
                ref="searchInput"
                v-model="searchQuery"
                class="global-text-input first-posts-modal__search-input"
                type="search"
                aria-label="Search places and events"
                placeholder="Search by name"
                :tabindex="isSearchExpanded ? 0 : -1"
                @blur="collapseSearchIfEmpty"
                @keydown.esc.stop="clearAndCollapseSearch"
              />
            </div>
          </div>
        </div>
        <div v-if="isLoading" class="first-posts-modal__loading" role="status">
          <LoadingIcon :size="20" />
          Loading your photos…
        </div>

        <div v-else-if="options.length" class="first-posts-modal__options-wrap">
          <ul v-if="filteredOptions.length" ref="optionsList" class="first-posts-modal__options" @scroll="updateOptionsScrollCue">
            <li v-for="option in filteredOptions" :key="option.key" class="first-posts-modal__option-item">
              <label class="first-posts-option" :class="{ 'first-posts-option--checked': pickedKeys.has(option.key) }">
                <LocationAvatar v-if="option.location" :src="option.location.icon" :name="option.location.name" :size="40" />
                <span v-else class="first-posts-option__album-avatar" aria-hidden="true">
                  <IconEmbedded name="folder_2" :size="20" color="currentColor" />
                </span>

                <span class="first-posts-option__copy">
                  <span class="first-posts-option__name-row">
                    <span class="first-posts-option__name">{{ option.title }}</span>
                    <LocationCategoryIcon v-if="option.location" :tagKind="option.location.tagKind" :category="option.location.category" :size="22" />
                  </span>
                  <span class="first-posts-option__meta">{{ photoCountLabel(option.photoCount) }}</span>
                </span>

                <input class="first-posts-option__checkbox" type="checkbox" :checked="pickedKeys.has(option.key)" :disabled="props.isSubmitting" @change="togglePick(option.key)" />
              </label>
            </li>
          </ul>
          <p v-else class="first-posts-modal__no-results">No matches found.</p>
          <div v-if="hasMoreOptionsBelow" class="first-posts-modal__scroll-cue" aria-hidden="true"></div>
        </div>
      </section>
    </template>

    <template #footer>
      <div class="first-posts-modal__footer">
        <SrpButton color="gray" fill="outlined" :isDisabled="props.isSubmitting" iconInset="0 auto auto auto" @click="emit('update:isVisible', false)">
          <template #icon><IconEmbedded name="arrow-left_3-5" :size="17" /></template>
          Back
        </SrpButton>
        <SrpButton color="teal" :isDisabled="props.isSubmitting" @click="create">
          <template #icon>
            <LoadingIcon v-if="props.isSubmitting" :size="18" />
            <IconEmbedded v-else name="ai_2" :size="18" />
          </template>
          {{ props.isSubmitting ? "Creating…" : "Create my first posts" }}
        </SrpButton>
      </div>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from "vue";
import type { Ref } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import { isScreenEqualOrSmaller } from "@helpers/ScreenSizeHelper";
import { buildFirstPostsRequest, buildFocusOptions, FIRST_POSTS_NOTE_MAX_LENGTH } from "@logic/FirstPostsFocusOptions";
import { locationTextMatches } from "@logic/LocationSearch";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";
import LocationCategoryIcon from "@components/SocialLocations/LocationCategoryIcon.vue";

// Types
import type { FirstPostsRequest } from "@contracts/contentCalendar";
import type { FocusOption } from "@logic/FirstPostsFocusOptions";
import type { ScreenSize } from "@contracts/screenSize";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    customerId: string;
    /** The parent is starting the first posts; everything is locked until it navigates away (or reports an error). */
    isSubmitting?: boolean;
  }>(),
  {
    isSubmitting: false,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "create", request: FirstPostsRequest): void;
}>();

// SrpModal's default content height leaves 120px for the header and footer. This modal's header has a subtitle, and on a phone
// the footer stacks its two buttons, so without more room the window ran past the top and bottom of the screen (no way to reach Back).
const screenSize = inject("screenSize", null) as Ref<ScreenSize> | null;
const contentMaxHeight = computed<string>(() => (screenSize?.value && isScreenEqualOrSmaller(screenSize.value, "mobile") ? "calc(100dvh - 290px)" : "calc(100dvh - 210px)"));

// Picks and note ============================================================
// Both survive Back, so a customer who goes back to add photos finds them as they left them.
const pickedKeys = ref<Set<string>>(new Set());
const note = ref<string>("");

function togglePick(key: string) {
  const next = new Set(pickedKeys.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  pickedKeys.value = next;
}

// Options ===================================================================
// Loaded each time the modal opens. The upload section keeps its own copies of the albums and locations
// (a location added there never reaches the page), so the page's lists can be out of date.
// See FirstPostsFocusOptions.ts for which options are offered and in what order.
const options = ref<Array<FocusOption>>([]);
const isLoading = ref<boolean>(false);
const searchQuery = ref("");
const searchInput = ref<HTMLInputElement | null>(null);
const isSearchExpanded = ref(false);
const filteredOptions = computed(() => options.value.filter(option => locationTextMatches([option.title], searchQuery.value)));
const optionsList = ref<HTMLUListElement | null>(null);
const hasMoreOptionsBelow = ref(false);
let optionsResizeObserver: ResizeObserver | null = null;
let loadGeneration = 0;

function updateOptionsScrollCue() {
  const list = optionsList.value;
  hasMoreOptionsBelow.value = !!list && list.scrollTop + list.clientHeight < list.scrollHeight - 2;
}

watch(
  optionsList,
  async list => {
    optionsResizeObserver?.disconnect();
    if (list) {
      optionsResizeObserver ??= new ResizeObserver(updateOptionsScrollCue);
      optionsResizeObserver.observe(list);
    }
    await nextTick();
    updateOptionsScrollCue();
  },
  { flush: "post" }
);

watch([options, isLoading], () => nextTick(updateOptionsScrollCue), { flush: "post" });
watch(
  searchQuery,
  async () => {
    await nextTick();
    optionsList.value?.scrollTo({ top: 0 });
    updateOptionsScrollCue();
  },
  { flush: "post" }
);
onBeforeUnmount(() => optionsResizeObserver?.disconnect());

async function openSearch() {
  isSearchExpanded.value = true;
  await nextTick();
  searchInput.value?.focus();
}

function collapseSearchIfEmpty() {
  if (!searchQuery.value.trim()) isSearchExpanded.value = false;
}

function clearAndCollapseSearch() {
  searchQuery.value = "";
  isSearchExpanded.value = false;
  searchInput.value?.blur();
}

async function loadOptions() {
  const generation = ++loadGeneration;
  // On a reopen, keep showing the last list while it refreshes.
  isLoading.value = options.value.length === 0;
  try {
    const [galleries, locations] = await Promise.all([ContentCalendarRepo.loadUploadGroups(props.customerId), SocialLocationsRepo.list(props.customerId, false, false)]);
    if (generation !== loadGeneration) return;
    options.value = buildFocusOptions(galleries, locations);
    // Drop picks that are no longer offered (ex. an album deleted after going Back)
    const offeredKeys = new Set(options.value.map(option => option.key));
    pickedKeys.value = new Set([...pickedKeys.value].filter(key => offeredKeys.has(key)));
  } catch (error) {
    // Non-blocking: the note and the button still work without the list.
    if (generation === loadGeneration) getGlobalRemoteLogger()?.error(`FirstPostsModal.loadOptions failed. customerId=${props.customerId} error=${error}`);
  } finally {
    if (generation === loadGeneration) isLoading.value = false;
  }
}

watch(
  () => props.isVisible,
  isVisible => {
    if (isVisible) loadOptions();
    else clearAndCollapseSearch();
  },
  { immediate: true }
);

function photoCountLabel(count: number): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? "photo" : "photos"}`;
}

// Create ====================================================================
function create() {
  if (props.isSubmitting) return;
  emit("create", buildFirstPostsRequest(options.value, pickedKeys.value, note.value));
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

// First posts modal ==========================================================
.first-posts-modal {
  &__header {
    gap: 6px;
    display: grid;
  }

  &__title {
    margin: 0;
  }

  &__subtitle {
    margin: 0;
    color: #5b5b5b;
  }

  &__loading {
    min-height: 64px;
    gap: 10px;
    display: flex;
    align-items: center;
    color: #667085;
    font: 400 14px/19px sans-serif;
  }

  &__options {
    // About five rows, so the choices fit in view on a laptop even with a long list.
    max-height: 372px;
    margin: 0;
    padding: 2px;
    gap: 10px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    overflow-y: auto;
    list-style: none;
  }

  &__options-wrap {
    position: relative;
  }

  &__scroll-cue {
    height: 48px;
    position: absolute;
    inset: auto 12px 0 0;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff 65%);
    pointer-events: none;
  }

  &__no-results {
    margin: 8px 2px;
    color: #667085;
  }

  &__option-item {
    min-width: 0;
    list-style: none;
  }

  &__note {
    // A global rule sets every label's margin to 0 !important.
    padding-bottom: 22px;
    gap: 6px;
    display: grid;
  }

  &__note-label,
  &__focus-title {
    color: #102f41;
    font: 600 15px/20px $font-family;
  }

  &__focus-heading {
    margin-bottom: 10px;
    gap: 8px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__focus-title {
    margin: 0;
  }

  &__search {
    gap: 4px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
  }

  &__search-toggle {
    width: 28px;
    height: 28px;
    padding: 0;
    flex-shrink: 0;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border: 0;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  }

  &__search-input-wrap {
    width: 0;
    opacity: 0;
    overflow: hidden;
    pointer-events: none;
    transition:
      width 0.2s ease,
      opacity 0.2s ease;

    &--open {
      width: 220px;
      opacity: 1;
      pointer-events: auto;
    }
  }

  &__search-input {
    width: 220px;
    height: 32px;
    padding: 4px 10px;
    font-size: 14px;
  }

  &__optional {
    color: #667085;
    font-weight: 400;
  }

  &__note-input {
    width: 100%;
    height: 58px;
    resize: vertical;
  }

  &__footer {
    width: 100%;
    gap: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

// One option =================================================================
.first-posts-option {
  min-height: 64px;
  padding: 11px 14px;
  gap: 12px;
  display: flex;
  align-items: center;
  border: 1px solid #e4e7ec;
  border-radius: 9px;
  background: #fff;
  cursor: pointer;
  transition:
    border-color 0.1s ease,
    background 0.1s ease;

  &:hover {
    border-color: #a9d3d3;
  }

  &--checked {
    border-color: #0d8a8c;
    background: #eef8f7;
  }

  // The native checkbox is the control; this shows its keyboard focus on the whole card.
  &:has(.first-posts-option__checkbox:focus-visible) {
    outline: 2px solid #087f82;
    outline-offset: 1px;
  }

  &__album-avatar {
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    color: #667085;
    background: #e4e7ec;
  }

  &__copy {
    min-width: 0;
    flex: 1;
    gap: 2px;
    display: grid;
  }

  &__name-row {
    min-width: 0;
    gap: 8px;
    display: flex;
    align-items: center;
  }

  &__name {
    min-width: 0;
    color: #102f41;
    font: 600 15px/20px $font-family;
    overflow-wrap: anywhere;
  }

  &__meta {
    color: #667085;
    font: 400 13px/18px sans-serif;
  }

  &__checkbox {
    width: 18px;
    height: 18px;
    margin: 0;
    flex-shrink: 0;
    accent-color: #0d8a8c;
    cursor: pointer;
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .first-posts-modal {
    // One column, and the modal itself scrolls (a second scroll area inside it is awkward on a phone).
    &__options {
      max-height: none;
      grid-template-columns: minmax(0, 1fr);
      overflow-y: visible;
    }

    // Both buttons don't fit side by side on a phone; stack them with Create on top.
    &__footer {
      flex-direction: column-reverse;
      align-items: stretch;

      > * {
        width: 100%;
      }
    }
  }
}
</style>
